import { Check, Globe, Mail, Phone } from 'lucide-react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import { Badge, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import type { ExtractedContact } from '../types';

interface ContactResultCardProps {
  contact: ExtractedContact;
  selected: boolean;
  onToggle: () => void;
}

const CONFIDENCE_COLORS: Record<string, 'green' | 'amber' | 'red'> = {
  high: 'green',
  medium: 'amber',
  low: 'red',
};

export function ContactResultCard({ contact, selected, onToggle }: ContactResultCardProps) {
  const { t } = useTranslation();
  const Icon = contact.type === 'email' ? Mail : Phone;

  return (
    <Pressable
      onPress={onToggle}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      accessibilityLabel={`${contact.type}: ${contact.value}`}
      style={[styles.card, selected && styles.cardSelected]}
    >
      <HStack spacing="md" style={{ alignItems: 'flex-start' }}>
        <VStack spacing="sm" style={{ flex: 1 }}>
          <HStack spacing="sm" style={{ alignItems: 'center' }}>
            <Icon size={18} color={selected ? colors.primary[500] : colors.text.tertiary} />
            <Text
              variant="body"
              weight={selected ? 'semibold' : 'normal'}
              color={selected ? colors.text.primary : colors.text.secondary}
            >
              {contact.value}
            </Text>
          </HStack>

          <HStack spacing="xs" style={{ flexWrap: 'wrap' }}>
            <Badge
              variant="subtle"
              color={CONFIDENCE_COLORS[contact.confidence] || 'neutral'}
              size="sm"
            >
              {t(`leads.webSearch.confidence.${contact.confidence}`)}
            </Badge>
            <Badge variant="outline" color="neutral" size="sm">
              {t(`leads.webSearch.sourceTypes.${contact.sourceType}`)}
            </Badge>
          </HStack>

          {contact.context && (
            <Text variant="caption" color={colors.text.tertiary} numberOfLines={3}>
              {contact.context}
            </Text>
          )}

          <HStack spacing="xs" style={{ alignItems: 'center' }}>
            <Globe size={12} color={colors.text.disabled} />
            <Text variant="caption" color={colors.text.disabled} numberOfLines={1}>
              {contact.sourceUrl}
            </Text>
          </HStack>
        </VStack>

        {selected && (
          <View style={styles.checkContainer}>
            <Check size={14} color={colors.primary[500]} />
          </View>
        )}
      </HStack>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
  },
  cardSelected: {
    borderColor: colors.primary[500],
  },
  checkContainer: {
    marginTop: 2,
  },
});
